import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import CareerOutcomesModule from 'bundles/degree-description/components/modules/career-outcomes/CareerOutcomesModule';
import FaqModule from 'bundles/degree-description/components/modules/faqs/FaqModule';
import HelpCenterBlock from 'bundles/degree-description/components/modules/faqs/HelpCenterBlock';
import AtAGlanceHorizontalBlock from 'bundles/degree-description/components/modules/hero/AtAGlanceHorizontalBlock';
import DeadlinesBlock from 'bundles/degree-description/components/modules/hero/DeadlinesBlock';
import EoiAtAGlanceBlock from 'bundles/degree-description/components/modules/hero/EoiAtAGlanceBlock';
import EoiButtonsBlock from 'bundles/degree-description/components/modules/hero/EoiButtonsBlock';
import HeroBanner from 'bundles/degree-description/components/modules/hero/HeroBanner';
import HeroModule from 'bundles/degree-description/components/modules/hero/HeroModule';
import InformationalSectionContainer from 'bundles/degree-description/components/modules/hero/InformationalSectionContainer';
import ProgramDetails from 'bundles/degree-description/components/modules/program-details/ProgramDetails';
import StackabilityModule from 'bundles/degree-description/components/modules/stackability/StackabilityModule';
import SchemaMarkup from 'bundles/degree-description/components/modules/structured-data-markup/SchemaMarkup';
import { HideOnDesktop, HideOnMobile } from 'bundles/degree-description/components/shared/BreakpointDisplay';
import ModuleContainerStyleWrap from 'bundles/degree-description/components/shared/ModuleContainerStyleWrap';
import RichText from 'bundles/degree-description/components/shared/RichText';
import type { PropsFromDegreePageContent } from 'bundles/degree-description/pages/consumer/degree-description-page/DegreePageContent';
import { PAGE_NAMES_MARKUP } from 'bundles/degree-description/utils/schema-markup';

import _t from 'i18n!nls/degree-description';

type Props = PropsFromDegreePageContent;

const Redesign2026SinglePage = ({ pageContent, eoiContainerText, eoiButtons, setEoiTrackingIndex }: Props) => {
  const { overviewAbout, backgroundImage, partner, programName, atAGlance, faqCollection, redesign2026Modules } =
    pageContent;

  const location = useLocation();

  return (
    <div>
      <SchemaMarkup pageName={PAGE_NAMES_MARKUP.overview} pathName={location.pathname} pageContent={pageContent} />

      <HeroModule
        informationalSection={
          <HideOnMobile>
            <InformationalSectionContainer
              content={
                <RichText
                  content={overviewAbout?.json}
                  contentLinks={overviewAbout?.links}
                  linkTrackingName="overviewAbout_body_link"
                  buttonTrackingNameV3="degree_info_link"
                />
              }
              sidebarContent={
                <>
                  <DeadlinesBlock finalDeadlineInfo={atAGlance?.finalDeadlineInfo} />
                </>
              }
            />
          </HideOnMobile>
        }
        heroBanner={
          <HeroBanner
            backgroundImageUrl={backgroundImage?.url ?? ''}
            partnerLogoUrl={partner?.partnerLogo?.url ?? ''}
            partnerName={partner?.partnerName ?? ''}
            programName={programName ?? ''}
            isOverviewPage
            sidebarContent={
              <EoiButtonsBlock
                eoiContainerText={eoiContainerText}
                eoiButtons={eoiButtons}
                trackingIndex={1}
                setEoiTrackingIndex={setEoiTrackingIndex}
              />
            }
          />
        }
        contentInBetween={
          <>
            <AtAGlanceHorizontalBlock atAGlance={atAGlance} />
            <HideOnDesktop>
              <EoiAtAGlanceBlock atAGlance={atAGlance} trackingIndex={1} />
            </HideOnDesktop>
          </>
        }
      />

      {redesign2026Modules?.careerOutcomes && (
        <ModuleContainerStyleWrap>
          <CareerOutcomesModule
            heading={redesign2026Modules?.careerOutcomes?.heading}
            valueProps={redesign2026Modules?.careerOutcomes?.valuePropsCollection?.items}
            rolesTitle={redesign2026Modules?.careerOutcomes?.rolesHeading}
            roles={redesign2026Modules?.careerOutcomes?.rolesCollection?.items}
            logosTitle={redesign2026Modules?.careerOutcomes?.logosHeading}
            logos={redesign2026Modules?.careerOutcomes?.whereGraduatesWorkLogosCollection?.items}
          />
        </ModuleContainerStyleWrap>
      )}

      <ModuleContainerStyleWrap>
        <ProgramDetails pageContent={pageContent} />
      </ModuleContainerStyleWrap>

      <StackabilityModule />

      {faqCollection?.items && faqCollection.items.length > 0 && (
        <ModuleContainerStyleWrap>
          <FaqModule
            header={_t('Frequently asked questions')}
            faqItems={faqCollection.items}
            sideColumnContent={<HelpCenterBlock />}
          />
        </ModuleContainerStyleWrap>
      )}
    </div>
  );
};

export default Redesign2026SinglePage;
